import React, { useState } from 'react';
import { Upload, message } from 'antd';
import { PlusOutlined, LoadingOutlined } from '@ant-design/icons';
import { CardShell, CardContent } from './card-selector';
import { C } from '../../constants/design-tokens';
import { useResponsiveSizes } from '../../../../hooks/use-responsive-sizes';

const MAX_FILE_SIZE = 32 * 1024 * 1024; // 32MB
const ALLOWED_EXTENSIONS = ['image/png', 'image/jpeg', 'image/webp'];

const validateFile = (file: File): boolean => {
  if (!ALLOWED_EXTENSIONS.includes(file.type)) {
    message.error('Solo se permiten archivos PNG, JPG o WEBP');
    return false;
  }
  if (file.size > MAX_FILE_SIZE) {
    message.error('El archivo no puede superar 32MB');
    return false;
  }
  return true;
};

/**
 * Extracted from left-panel.tsx UploadCard.
 * Reusable upload trigger that fits inside a CardSelector grid.
 * Uses CardShell + CardContent from card-selector.tsx for visual consistency.
 */
export const UploadCard: React.FC<{
  isUploading: boolean;
  uploadProgress?: string;
  onUpload: (files: File[]) => void;
  disabled?: boolean;
}> = ({ isUploading, uploadProgress, onUpload, disabled = false }) => {
  const [hovered, setHovered] = useState(false);
  const { fontSize } = useResponsiveSizes();

  const card = (
    <CardShell
      border={`1px dashed ${hovered && !disabled ? C.accent : C.border}`}
      background={hovered && !disabled ? C.accentSurface : C.surface}
      cursor={disabled ? 'not-allowed' : isUploading ? 'wait' : 'pointer'}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <CardContent>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 4,
            color: disabled ? C.textDisabled : hovered ? C.accent : C.textMuted,
            opacity: disabled ? 0.5 : 1,
            transition: 'color 0.15s ease'
          }}
        >
          {isUploading ? <LoadingOutlined /> : <PlusOutlined />}
          <span style={{ fontSize: fontSize.xs, fontWeight: 500 }}>
            {isUploading && uploadProgress ? uploadProgress : 'Subir'}
          </span>
        </div>
      </CardContent>
    </CardShell>
  );

  if (disabled) return card;

  return (
    <Upload
      multiple
      showUploadList={false}
      accept='.png,.jpg,.jpeg,.webp'
      beforeUpload={(file, fileList) => {
        // Ant Design calls beforeUpload per file; trigger onUpload on the last one
        if (file === fileList[fileList.length - 1]) {
          const valid = fileList.filter(f => validateFile(f as File)) as File[];
          if (valid.length > 0) onUpload(valid);
        }
        return false;
      }}
    >
      {card}
    </Upload>
  );
};
